import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Checkbox, FormControlLabel,
  Divider, List, ListItem, ListItemText, ListItemIcon,
  CircularProgress, Alert, Paper, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  Replay as ReplayIcon,
  EmojiEvents as RankingIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

const POLLING_INTERVAL_MS = 5000;

// Ranking psicolaboral ATS: comparación allVsAll de los informes psicolaborales
// completados de la vacante. Reutiliza el backend legacy de "Comparación
// Evaluaciones" (POST /comparisons), sin cambios de contrato.
const AtsPsycholaboralComparisonDialog = ({ open, onClose, positionId, allApplications = [] }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const { authToken, organization, project, showAlert } = useStore.getState();

  const [view, setView] = useState('list');
  const [comparisons, setComparisons] = useState([]);
  const [loadingComparisons, setLoadingComparisons] = useState(false);
  const [selectedComparison, setSelectedComparison] = useState(null);
  const [completedReports, setCompletedReports] = useState({});
  const [loadingReports, setLoadingReports] = useState(false);
  const [applications, setApplications] = useState(allApplications);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [selectedAppIds, setSelectedAppIds] = useState([]);
  const [launching, setLaunching] = useState(false);
  const pollingRef = useRef(null);

  // Candidatos con informe psicolaboral vinculado; elegibles = informe completado
  const candidates = applications
    .filter((a) => a.psycholaboralReportId)
    .map((a) => ({
      app: a,
      appId: a.uid || a.id,
      report: completedReports[a.psycholaboralReportId] || null,
    }));
  const eligible = candidates.filter((c) => c.report);

  useEffect(() => {
    if (open) {
      setView('list');
      setSelectedComparison(null);
      setSelectedAppIds([]);
      loadComparisons();
      loadReports();
      loadApplications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Recargar las applications al abrir: el psycholaboralReportId puede haberse
  // vinculado después de que la página cargó (ej: psicolaboral recién creado)
  const loadApplications = async () => {
    try {
      setLoadingApplications(true);
      const response = await api.get(`/applications`, {
        params: { positionId },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        setApplications(response.data.response.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingApplications(false);
    }
  };

  // Se usa /comparisons/by-project (patrón legacy) y se filtra por vacante;
  // GET /comparisons (getAll) no está implementado en el modelo del backend
  const loadComparisons = async () => {
    try {
      setLoadingComparisons(true);
      const response = await api.get(`/comparisons/by-project`, {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.errorCode === 0) {
        const all = (response.data.response?.data || []).filter(
          (c) => c.position_id === positionId
        );
        setComparisons(all);
        return all;
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingComparisons(false);
    }
    return [];
  };

  // Informes psicolaborales completados del proyecto (mapa reportId → report),
  // mismo criterio que el legacy: status 'completado' + structured_data
  const loadReports = async () => {
    try {
      setLoadingReports(true);
      const { data } = await api.get(`/reports`, {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const list = data.data || [];
      const map = {};
      list.forEach((report) => {
        if (
          report.status === 'completado' &&
          report.structured_data &&
          Object.keys(report.structured_data).length > 0
        ) {
          map[report.id] = report;
        }
      });
      setCompletedReports(map);
    } catch (err) {
      console.error(err);
      setCompletedReports({});
    } finally {
      setLoadingReports(false);
    }
  };

  // Polling mientras haya comparaciones pendientes/procesando (patrón legacy:
  // las comparaciones no usan onSnapshot)
  useEffect(() => {
    const hasInProgress = comparisons.some((c) => ['pendiente', 'procesando'].includes(c.status));
    if (!open || !hasInProgress) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return undefined;
    }
    if (pollingRef.current) return undefined;
    pollingRef.current = setInterval(async () => {
      const updated = await loadComparisons();
      setSelectedComparison((prev) => {
        if (!prev) return prev;
        return updated.find((c) => c.id === prev.id) || prev;
      });
    }, POLLING_INTERVAL_MS);
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisons, open]);

  const handleOpenCreate = () => {
    // Todos los elegibles vienen preseleccionados (requerimiento)
    setSelectedAppIds(eligible.map((c) => c.appId));
    setView('create');
  };

  const handleSelectAll = (checked) => {
    setSelectedAppIds(checked ? eligible.map((c) => c.appId) : []);
  };

  const handleToggleCandidate = (appId) => {
    setSelectedAppIds((prev) =>
      prev.includes(appId) ? prev.filter((id) => id !== appId) : [...prev, appId]
    );
  };

  // Mapa { nombreCandidato: reportId } que espera el backend; se desambiguan
  // nombres repetidos con el email
  const buildSelectedReports = () => {
    const selected = eligible.filter((c) => selectedAppIds.includes(c.appId));
    const map = {};
    selected.forEach((c) => {
      let key = c.app.candidateName || c.app.candidateEmail || c.app.applicantId;
      if (map[key]) key = `${key} (${c.app.candidateEmail || c.appId})`;
      map[key] = c.app.psycholaboralReportId;
    });
    return map;
  };

  const handleLaunch = async () => {
    const selectedReports = buildSelectedReports();
    if (Object.keys(selectedReports).length < 2) {
      showAlert(t('ats.psychoComparison.notEnough'), 'warning');
      return;
    }
    setLaunching(true);
    try {
      // Chequeo de límite de uso (misma acción que el legacy)
      try {
        const { data: usageData } = await api.get(
          `/organizations/${organization}/usage/check`,
          { params: { action: 'comparisons' }, headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (usageData.response?.status_response === 'OK' && usageData.response?.canPerform === false) {
          showAlert(t('ats.psychoComparison.limitReached'), 'error');
          setLaunching(false);
          return;
        }
      } catch (usageErr) {
        console.error(usageErr); // si el check falla, se permite continuar (patrón legacy)
      }

      const { data } = await api.post(
        `/comparisons`,
        {
          position_id: positionId,
          comparison_type: 'allVsAll',
          ideal_applicant: null,
          selected_reports: selectedReports,
          project,
          organization,
          language: lang,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (data.response?.status_response === 'OK') {
        showAlert(t('ats.psychoComparison.launched'), 'success');
        setSelectedAppIds([]);
        await loadComparisons();
        setView('list');
      } else {
        showAlert(data.response?.message || t('ats.psychoComparison.error'), 'error');
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 409) {
        showAlert(t('ats.psychoComparison.limitReached'), 'error');
      } else {
        showAlert(err.response?.data?.response?.message || t('ats.psychoComparison.error'), 'error');
      }
    } finally {
      setLaunching(false);
    }
  };

  const handleRetry = async (comparisonId) => {
    try {
      await api.post(
        `/comparisons/${comparisonId}/retry`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      await loadComparisons();
    } catch (err) {
      console.error(err);
      showAlert(t('ats.psychoComparison.error'), 'error');
    }
  };

  const getStatusChip = (status) => {
    const config = {
      pendiente:  { label: t('ats.psychoComparison.statusPending'),    color: 'default' },
      procesando: { label: t('ats.psychoComparison.statusProcessing'), color: 'warning' },
      completado: { label: t('ats.psychoComparison.statusCompleted'),  color: 'success' },
      error:      { label: t('ats.psychoComparison.statusError'),      color: 'error' },
    };
    const c = config[status] || config.pendiente;
    return <Chip label={c.label} color={c.color} size="small" />;
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    if (ts.toDate) return ts.toDate().toLocaleDateString('es-CL');
    if (ts._seconds) return new Date(ts._seconds * 1000).toLocaleDateString('es-CL');
    return new Date(ts).toLocaleDateString('es-CL');
  };

  const affinityChipSx = (value) => ({
    backgroundColor: value >= 70 ? '#DCFCE7' : value >= 50 ? '#FEF9C3' : '#FEE2E2',
    color: value >= 70 ? '#16A34A' : value >= 50 ? '#CA8A04' : '#DC2626',
    fontWeight: 700,
  });

  const sortedResults = (comparison) => {
    const results = comparison?.result?.applicant_results || [];
    return [...results].sort((a, b) => {
      if (a.ranking_position && b.ranking_position) return a.ranking_position - b.ranking_position;
      return (b.affinity_percentage || 0) - (a.affinity_percentage || 0);
    });
  };

  return (
    <Dialog open={open} onClose={() => { if (!launching) onClose(); }} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {view !== 'list' && (
            <IconButton size="small" onClick={() => setView('list')} disabled={launching}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="h6">
            {view === 'list'    && t('ats.psychoComparison.dialogTitle')}
            {view === 'create'  && t('ats.psychoComparison.newComparison')}
            {view === 'results' && t('ats.psychoComparison.ranking')}
          </Typography>
        </Box>
        {view === 'list' && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            disabled={loadingReports || loadingApplications}
            sx={{ backgroundColor: '#0D9488', '&:hover': { backgroundColor: '#0F766E' } }}
          >
            {t('ats.psychoComparison.newComparison')}
          </Button>
        )}
      </DialogTitle>

      <DialogContent sx={{ minHeight: 300 }}>

        {/* LISTA */}
        {view === 'list' && (
          <>
            {loadingComparisons ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : comparisons.length === 0 ? (
              <Alert severity="info">{t('ats.psychoComparison.noComparisons')}</Alert>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{t('ats.psychoComparison.tableCandidates')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('ats.psychoComparison.tableStatus')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('ats.psychoComparison.tableDate')}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>{t('actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparisons.map((comparison) => (
                      <TableRow key={comparison.id} hover>
                        <TableCell><Typography variant="caption">{comparison.applicants?.length || 0}</Typography></TableCell>
                        <TableCell>{getStatusChip(comparison.status)}</TableCell>
                        <TableCell><Typography variant="caption">{formatDate(comparison.created_at)}</Typography></TableCell>
                        <TableCell align="center">
                          {['pendiente', 'procesando'].includes(comparison.status) && <CircularProgress size={16} />}
                          {comparison.status === 'completado' && (
                            <IconButton size="small" color="primary" onClick={() => { setSelectedComparison(comparison); setView('results'); }}>
                              <AssessmentIcon fontSize="small" />
                            </IconButton>
                          )}
                          {comparison.status === 'error' && (
                            <IconButton size="small" color="warning" onClick={() => handleRetry(comparison.id)}>
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {/* CREAR */}
        {view === 'create' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('ats.psychoComparison.dialogSubtitle')}
            </Typography>
            {loadingReports || loadingApplications ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : candidates.length === 0 || eligible.length === 0 ? (
              <Alert severity="info">{t('ats.psychoComparison.noEligible')}</Alert>
            ) : (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedAppIds.length === eligible.length && eligible.length > 0}
                      indeterminate={selectedAppIds.length > 0 && selectedAppIds.length < eligible.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2" fontWeight={600}>{t('ats.psychoComparison.selectAll')}</Typography>}
                />
                <Divider sx={{ my: 1 }} />
                <List dense>
                  {candidates.map((c) => {
                    const isEligible = !!c.report;
                    return (
                      <ListItem key={c.appId} disablePadding>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            size="small"
                            checked={selectedAppIds.includes(c.appId)}
                            onChange={() => handleToggleCandidate(c.appId)}
                            disabled={!isEligible}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" color={isEligible ? 'inherit' : 'text.disabled'}>
                              {c.app.candidateName || '—'}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {c.app.candidateEmail}
                              {!isEligible && ` — ${t('ats.psychoComparison.reportNotReady')}`}
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </>
            )}
          </>
        )}

        {/* RESULTADOS */}
        {view === 'results' && selectedComparison && (
          <Box>
            <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('ats.psychoComparison.totalCandidates')}</Typography>
                  <Typography variant="h6">{selectedComparison.applicants?.length || 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('ats.psychoComparison.tableDate')}</Typography>
                  <Typography variant="h6">{formatDate(selectedComparison.created_at)}</Typography>
                </Box>
              </Box>
            </Paper>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>{t('ats.psychoComparison.ranking')}</Typography>

            {sortedResults(selectedComparison).length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {sortedResults(selectedComparison).map((result, index) => (
                  <Paper key={result.applicant_name || index} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: index === 0 ? '#F59E0B' : index === 1 ? '#9CA3AF' : index === 2 ? '#92400E' : '#E5E7EB', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0 }}>
                          {index + 1}
                        </Box>
                        <Typography variant="body2" fontWeight={600}>{result.applicant_name}</Typography>
                      </Box>
                      <Chip
                        label={`${result.affinity_percentage || 0}%`}
                        size="small"
                        sx={affinityChipSx(result.affinity_percentage || 0)}
                      />
                    </Box>
                    {result.job_fit_analysis && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {result.job_fit_analysis}
                      </Typography>
                    )}
                    {result.key_strengths?.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {result.key_strengths.slice(0, 3).map((s, i) => (
                          <Chip key={i} label={s} size="small" color="success" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                        ))}
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            ) : (
              <Alert severity="info">{t('ats.psychoComparison.noResults')}</Alert>
            )}

            {selectedComparison.result?.overall_recommendation && (
              <Paper elevation={0} sx={{ mt: 2, p: 1.5, backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 2 }}>
                <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                  {t('ats.psychoComparison.overallRecommendation')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedComparison.result.overall_recommendation}
                </Typography>
              </Paper>
            )}

            {selectedComparison.result?.wordDocumentUrl && (
              <Button
                href={selectedComparison.result.wordDocumentUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
              >
                {t('ats.psychoComparison.downloadReport')}
              </Button>
            )}
          </Box>
        )}

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={launching}>{t('close')}</Button>
        {view === 'create' && (
          <Button
            variant="contained"
            onClick={handleLaunch}
            disabled={selectedAppIds.length < 2 || launching}
            startIcon={launching ? <CircularProgress size={16} color="inherit" /> : <RankingIcon />}
            sx={{ backgroundColor: '#0D9488', '&:hover': { backgroundColor: '#0F766E' } }}
          >
            {launching ? t('ats.psychoComparison.launching') : t('ats.psychoComparison.launch', { count: selectedAppIds.length })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AtsPsycholaboralComparisonDialog;
